import { useEffect, useState } from 'react';
import type { AppSession, MeView, PatientSummary, PracticeView, ThreadSummary } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, greeting, pct } from '../../lib/format';
import { patientHash, threadHash } from '../../lib/routes';
import { Icon } from '../../components/HeartMark';
import { Avatar, Card, Empty } from '../../components/Bits';
import { ActivityList, GoalPill, GoalsMet, type FeedItem } from '../../components/Goals';
import { AlertList } from '../../components/Alerts';

const DAY = 86_400_000;

function flagRank(s: PatientSummary): number {
  if (s.flags.some((f) => f.level === 'urgent')) return 3;
  if (s.flags.some((f) => f.level === 'attention')) return 2;
  return 0;
}

export interface Attention {
  patient: PatientSummary;
  reasons: string[];
}

/** Who needs the doctor this week: flagged first, then whoever is missing the most goals, then whoever has gone quiet. */
export function needsAttention(patients: readonly PatientSummary[], now = Date.now()): Attention[] {
  const out: Attention[] = [];
  for (const p of patients) {
    const reasons = p.flags.filter((f) => f.level !== 'info').map((f) => f.text);
    const missed = p.goals.filter((g) => g.met === false).map((g) => g.label.toLowerCase());
    if (missed.length > 0) reasons.push(`Missing ${missed.join(', ')}`);
    if (p.lastActiveAt !== null && now - p.lastActiveAt > 2 * DAY) reasons.push(`Quiet for ${Math.floor((now - p.lastActiveAt) / DAY)} days`);
    if (reasons.length > 0) out.push({ patient: p, reasons });
  }
  const unmet = (p: PatientSummary) => p.goals.filter((g) => g.met === false).length;
  return out.sort((a, b) => flagRank(b.patient) - flagRank(a.patient) || unmet(b.patient) - unmet(a.patient) || a.patient.name.localeCompare(b.patient.name));
}

/** Everyone's latest activity, merged into one feed. */
export function practiceFeed(patients: readonly PatientSummary[], limit = 12): FeedItem[] {
  return patients
    .flatMap((p) => p.recentActivity.map((a) => ({ ...a, name: p.name, href: patientHash(p.patientId) })))
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
}

/** The doctor's front page: how the practice's patients are doing, who is keeping their goals, and what they have written. */
export function DoctorDashboard({ session, me, practice, onChanged, say }: { session: AppSession; me: MeView; practice: PracticeView; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .threads(session.token)
      .then((r) => alive && setThreads(r.threads))
      .catch(() => alive && setThreads([]));
    return () => {
      alive = false;
    };
  }, [session.token, practice.unreadMessages]);

  const patients = practice.patients;
  const attention = needsAttention(patients);
  const onTrack = patients.filter((p) => p.goalsTotal > 0 && p.goalsMet === p.goalsTotal).length;
  const open = practice.alerts.filter((a) => !a.ackedAt);
  const byName = [...patients].sort((a, b) => a.name.localeCompare(b.name));

  const ack = async (id: string) => {
    try {
      await api.ackAlert(id, session.token);
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not acknowledge that alert.');
    }
  };

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>
            {greeting(new Date().getHours())}, {me.name}
          </h1>
          <span className="hint">
            {practice.name} · {patients.length} {patients.length === 1 ? 'patient' : 'patients'}
          </span>
        </div>
        <a className="button primary" href="#/practice/messages">
          <Icon name="chat" size={16} /> Messages{practice.unreadMessages > 0 ? ` · ${practice.unreadMessages} unread` : ''}
        </a>
      </header>

      <div className="stat-grid">
        <Card className="stat-card">
          <span className="hint">Patients</span>
          <strong>{patients.length}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Active this week</span>
          <strong>{practice.stats.active7d}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Keeping every goal</span>
          <strong>
            {onTrack}
            <small> of {patients.length}</small>
          </strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Avg adherence</span>
          <strong>{pct(practice.stats.avgAdherence)}</strong>
        </Card>
        <Card className={`stat-card ${practice.stats.urgent > 0 ? 'urgent' : practice.stats.flagged > 0 ? 'attention' : ''}`}>
          <span className="hint">Flagged</span>
          <strong>
            {practice.stats.flagged}
            {practice.stats.urgent > 0 ? <small> · {practice.stats.urgent} urgent</small> : null}
          </strong>
        </Card>
        <Card className={`stat-card ${practice.unreadMessages > 0 ? 'attention' : ''}`}>
          <span className="hint">Unread messages</span>
          <strong>{practice.unreadMessages}</strong>
        </Card>
      </div>

      {patients.length === 0 ? (
        <Card>
          <Empty title="No patients yet">When your clinical manager invites patients and they create their accounts, they appear here.</Empty>
        </Card>
      ) : (
        <div className="two-col">
          <div className="stack">
            <Card>
              <h2>Needs attention</h2>
              {attention.length === 0 ? <p className="hint">Everyone is keeping their goals this week.</p> : null}
              <ul className="attention">
                {attention.slice(0, 8).map(({ patient: p, reasons }) => (
                  <li key={p.patientId} className={flagRank(p) === 3 ? 'urgent' : flagRank(p) === 2 ? 'attention' : ''}>
                    <a className="patient-cell" href={patientHash(p.patientId)}>
                      <Avatar name={p.name} size="sm" />
                      <span className="row-main">
                        <strong>{p.name}</strong>
                        <span className="hint">{reasons.join(' · ')}</span>
                      </span>
                    </a>
                    <GoalsMet met={p.goalsMet} total={p.goalsTotal} />
                  </li>
                ))}
              </ul>
            </Card>

            <AlertList alerts={open} patientHref={patientHash} onAcknowledge={(id: string) => void ack(id)} />

            <Card className="table-card">
              <div className="card-pad">
                <h2>Goals this week</h2>
                <span className="hint">Blood pressure below target on average, the step goal on 5 of 7 days, and doses taken as prescribed.</span>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Blood pressure</th>
                      <th>Steps</th>
                      <th>Adherence</th>
                      <th>Overall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byName.map((p) => (
                      <tr key={p.patientId}>
                        <td>
                          <a href={patientHash(p.patientId)}>{p.name}</a>
                        </td>
                        {(['bp', 'steps', 'adherence'] as const).map((kind) => {
                          const g = p.goals.find((x) => x.kind === kind);
                          return <td key={kind}>{g ? <GoalPill goal={g} /> : <span className="hint">—</span>}</td>;
                        })}
                        <td>
                          <GoalsMet met={p.goalsMet} total={p.goalsTotal} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="stack">
            <Card>
              <div className="card-head">
                <h2>Messages from patients</h2>
                <a className="quiet small" href="#/practice/messages">
                  All messages
                </a>
              </div>
              {threads === null ? <p className="hint">Loading…</p> : null}
              {threads !== null && threads.length === 0 ? <p className="hint">No messages yet. When a patient writes to the care team it lands here.</p> : null}
              <ul className="threads compact">
                {(threads ?? []).slice(0, 5).map((t) => (
                  <li key={t.patientId}>
                    <a className={`thread-item ${t.unread > 0 ? 'unread' : ''}`} href={threadHash(t.patientId)}>
                      <Avatar name={t.patientName} size="sm" />
                      <span className="row-main">
                        <strong>{t.patientName}</strong>
                        <span className="hint">
                          {t.lastFrom === 'patient' ? '' : 'You: '}
                          {t.lastText}
                        </span>
                      </span>
                      <span className="thread-side">
                        <span className="hint small">{fmtAgo(t.lastAt)}</span>
                        {t.unread > 0 ? <span className="badge badge-red">{t.unread} new</span> : null}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <h2>Recent activity</h2>
              <ActivityList items={practiceFeed(patients)} empty="No activity logged yet." />
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
